import { fromPolar, segment, vec } from '../geom';
import type { CalloutDimensionNode } from '../model';
import { leaderText } from './parts';
import type { DimensionDefinition } from './types';

/** Longer labels are measured as this long when only their length is known. */
const MAX_LABEL = 1000;

/**
 * A label on an object: a plain leader, with no arrowhead, from a point on the object out
 * along `angle`, and the label riding it past its end, as a radius's text does outside its
 * circle. It measures nothing, so its value is null.
 */
export const CALLOUT: DimensionDefinition<CalloutDimensionNode> = {
  label: 'Callout',
  arity: 6,
  // The label's length is recorded too, so a view can size a callout before its text arrives.
  fromRecord: ([x, y, angle, length, placed, size], kind, label) => ({
    kind,
    anchor: vec(x!, y!),
    angle: angle!,
    length: placed ? length! : null,
    label: label ?? 'x'.repeat(Math.min(Math.max(0, Math.round(size!)), MAX_LABEL)),
  }),
  resolve(node, look) {
    if (node.label === '') return 'A callout needs text';
    if (node.length !== null && !(node.length > 0)) return "A callout's length must be positive";
    const direction = fromPolar(vec(0, 0), 1, node.angle);
    const from = node.length ?? look.textHeight * 2;
    const leader = leaderText(node.anchor, direction, node.angle, from, node.label, look);
    return {
      value: null,
      layout: { strokes: [segment(node.anchor, leader.end)], fills: [], text: leader.text },
    };
  },
};
